import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ChevronRight,
  Package,
  ShoppingCart,
  Wrench,
} from "lucide-react";
import { useMemo } from "react";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import {
  usePurchaseOrdersListQuery,
  useShopFitmentPendingCountQuery,
  useShopInventorySummaryQuery,
} from "../api/queries";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;
const PURCHASE_ORDERS_ROUTE = "/app/shops/$shopId/purchase-orders" as const;
const MARKETPLACE_ROUTE = "/app/shops/$shopId/marketplace" as const;

const PO_MIN_SEARCH = {
  offset: 0,
  limit: 1,
} as const;

type AttentionRow = {
  key: string;
  to:
    | typeof INVENTORY_ROUTE
    | typeof PURCHASE_ORDERS_ROUTE
    | typeof MARKETPLACE_ROUTE;
  params: { shopId: string };
  search?: {
    offset?: number;
    limit?: number;
    status?: string;
    due?: "soon" | "overdue";
    filters?: { field: string; op: string; value: string }[];
  };
  icon: typeof Package;
  title: string;
  subtitle: string;
  count: number;
  variant: "default" | "amber" | "destructive";
};

/**
 * C — ລາຍການ “ສິ່ງທີ່ຄວນສົນໃຈ” ຈາກ API ທີ່ມີ (ບໍ່ມີ endpoint ໃໝ່)
 */
export function ShopDashboardAttentionSection({
  shopId,
}: {
  shopId: string | undefined;
}) {
  const { currentShop } = useMerchantShops(shopId);
  const flags = currentShop?.featureFlags;
  const verified = currentShop?.verifiedLevel ?? null;
  const perms = currentShop?.permissions ?? [];

  const canInv = canUseMerchantPermission(
    perms,
    MerchantPermissions.inventory.read,
    verified,
  );
  const canCatalog = canUseMerchantPermission(
    perms,
    MerchantPermissions.catalog.read,
    verified,
  );

  const invEnabled = Boolean(shopId) && hasShopFeature(flags, "inventory");
  const procEnabled = Boolean(shopId) && hasShopFeature(flags, "procurement");
  const marketplaceEnabled =
    Boolean(shopId) && hasShopFeature(flags, "marketplace_sync");

  const invSummary = useShopInventorySummaryQuery(
    invEnabled && canInv ? shopId : undefined,
  );

  const poOverdueQ = useMemo(
    () => ({ ...PO_MIN_SEARCH, due: "overdue" as const }),
    [],
  );
  const poSoonQ = useMemo(
    () => ({ ...PO_MIN_SEARCH, due: "soon" as const }),
    [],
  );
  const poOverdue = usePurchaseOrdersListQuery(
    procEnabled && canInv ? shopId : undefined,
    poOverdueQ,
  );
  const poDueSoon = usePurchaseOrdersListQuery(
    procEnabled && canInv ? shopId : undefined,
    poSoonQ,
  );

  const fitmentPending = useShopFitmentPendingCountQuery(
    marketplaceEnabled && canCatalog ? shopId : undefined,
  );

  const rows = useMemo((): AttentionRow[] => {
    if (!shopId) return [];
    const out: AttentionRow[] = [];

    if (invEnabled && canInv) {
      const n = invSummary.data?.lowStockAlerts ?? 0;
      if (n > 0) {
        out.push({
          key: "low-stock",
          to: INVENTORY_ROUTE,
          params: { shopId },
          icon: Package,
          title: "ແຈ້ງເຕືອນສະຕ໋ອກຕໍ່າ",
          subtitle: "ແຖວສະຕ໋ອກເຫຼືອ ≤ 5 ຊິ້ນຕໍ່ສາຂາ",
          count: n,
          variant: n > 10 ? "destructive" : "amber",
        });
      }
    }

    if (procEnabled && canInv) {
      const over = poOverdue.data?.meta.total ?? 0;
      if (over > 0) {
        out.push({
          key: "po-overdue",
          to: PURCHASE_ORDERS_ROUTE,
          params: { shopId },
          search: { offset: 0, limit: 20, due: "overdue" },
          icon: ShoppingCart,
          title: "PO ເກີນກຳນົດສົ່ງ",
          subtitle: "ກວດຮັບຕາມກຳນົດ",
          count: over,
          variant: "destructive",
        });
      }
      const soon = poDueSoon.data?.meta.total ?? 0;
      if (soon > 0) {
        out.push({
          key: "po-soon",
          to: PURCHASE_ORDERS_ROUTE,
          params: { shopId },
          search: { offset: 0, limit: 20, due: "soon" },
          icon: ShoppingCart,
          title: "PO ໃກ້ຄົບກຳນົດ (7 ວັນ)",
          subtitle: "ລາຍການໃກ້ຄວບຄິດ",
          count: soon,
          variant: "amber",
        });
      }
    }

    if (marketplaceEnabled && canCatalog) {
      const pending = fitmentPending.data?.count ?? 0;
      if (pending > 0) {
        out.push({
          key: "fitment-pending",
          to: MARKETPLACE_ROUTE,
          params: { shopId },
          search: {
            offset: 0,
            limit: 20,
          },
          icon: Wrench,
          title: "ລໍ Admin ຄີ fitment",
          subtitle: "SKU ຂາຍ POS ໄດ້ແຕ່ຍັງຂຶ້ນ Marketplace ບໍ່ໄດ້",
          count: pending,
          variant: "amber",
        });
      }
    }

    return out;
  }, [
    shopId,
    invEnabled,
    canInv,
    invSummary.data?.lowStockAlerts,
    procEnabled,
    poOverdue.data?.meta.total,
    poDueSoon.data?.meta.total,
    marketplaceEnabled,
    canCatalog,
    fitmentPending.data?.count,
  ]);

  const loading =
    (invEnabled && canInv && invSummary.isLoading) ||
    (procEnabled && canInv && (poOverdue.isLoading || poDueSoon.isLoading)) ||
    (marketplaceEnabled && canCatalog && fitmentPending.isLoading);

  if (!shopId) {
    return null;
  }

  return (
    <Card className="mb-6 overflow-hidden rounded-2xl border-amber-500/20 bg-linear-to-b from-amber-500/5 to-card shadow-sm ring-1 ring-amber-500/10">
      <CardHeader className="pb-2 sm:pb-3">
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-300">
            <AlertTriangle className="size-5" aria-hidden />
          </span>
          <div>
            <CardTitle className="text-base leading-snug sm:text-lg">
              ສິ່ງທີ່ຄວນສົນໃຈ
            </CardTitle>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະຫຼຸບຈາກສຕ໋ອກ ແລະ PO — ຕາມ feature ແລະສິດ
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-border/80 border-dashed bg-muted/20 py-8 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-background text-muted-foreground shadow-sm">
              <Package className="size-6" aria-hidden />
            </span>
            <p className="text-muted-foreground text-sm">
              ບໍ່ມີລາຍການເຕືອນ — ທຸກຢ່າງປົກກະຕິ
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/80 overflow-hidden rounded-xl border border-border/80 bg-card/50">
            {rows.map((r) => {
              const Icon = r.icon;
              const countClass =
                r.variant === "destructive"
                  ? "text-destructive"
                  : r.variant === "amber"
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-foreground";
              return (
                <li key={r.key}>
                  <Link
                    to={r.to}
                    params={r.params}
                    {...(r.search != null ? { search: r.search } : {})}
                    className="flex items-center gap-3 px-3 py-3.5 transition-colors hover:bg-muted/60"
                  >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted/80 text-muted-foreground">
                      <Icon className="size-4" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground text-sm">
                        {r.title}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {r.subtitle}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-md bg-background/80 px-2 py-0.5 font-semibold text-sm tabular-nums ${countClass} ring-1 ring-border/50`}
                    >
                      {r.count}
                    </span>
                    <ChevronRight
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
